// Orders, receipts and subscriptions — the reading half.
//
// busy-ai-srv#15 / AI Brain spec Appendix §7 (orders), §8 (receipts), §9 (subscriptions). The three
// list views are thin on purpose: everything that decides what a row SAYS — its title, its second
// line, its money, its status word and which actions it offers — lives here as plain functions over
// plain objects, so it can be asserted without mounting anything.
//
// Nothing in this module fetches, and nothing knows about Vuex. A row is a value.

/** The order statuses the service stores, in the order an order moves through them. */
export const ORDER_STATUSES = ['placed', 'shipped', 'delivered', 'cancelled']

/** How often a subscription sends. 'unknown' is an answer, not an absence. */
export const FREQUENCIES = ['daily', 'weekly', 'monthly', 'unknown']

/** The words the UI uses. One place, because a status word appears in a row, a filter and a title. */
export const STATUS_LABELS = {
  placed: 'Placed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

export const FREQUENCY_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  unknown: 'Occasionally',
}

/** The symbol a currency is written with, when it has one. Anything else keeps its code. */
const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€', TRY: '₺', JPY: '¥' }

/**
 * Money as the product writes it: symbol first when the currency has one, the code after the number
 * when it does not, and always two decimals — "12.5" in a spend report reads as an unfinished number.
 *
 * Returns '' for an absent amount rather than '£0.00': an order whose total was never stated has no
 * total, and printing zero would be inventing one.
 */
export function formatMoney(amount, currency) {
  if (amount === null || amount === undefined || amount === '') return ''
  const value = Number(amount)
  if (!Number.isFinite(value)) return ''

  const code = String(currency || '').trim().toUpperCase()
  const fixed = value.toFixed(2)
  const symbol = CURRENCY_SYMBOLS[code]
  if (symbol) return `${symbol}${fixed}`
  return code ? `${fixed} ${code}` : fixed
}

/** The status word, or 'Placed' for anything unrecognised — every order is at least placed. */
export function statusLabel(status) {
  const key = String(status || '').trim().toLowerCase()
  return STATUS_LABELS[key] || STATUS_LABELS.placed
}

/**
 * The tone a status is drawn in. Names, not colours: the view maps them to the Tailwind classes the
 * rest of the app already uses, so this module never decides what green means.
 */
export function statusTone(status) {
  switch (String(status || '').trim().toLowerCase()) {
    case 'delivered': return 'done'
    case 'shipped': return 'progress'
    case 'cancelled': return 'muted'
    default: return 'new'
  }
}

export function frequencyLabel(frequency) {
  const key = String(frequency || '').trim().toLowerCase()
  return FREQUENCY_LABELS[key] || FREQUENCY_LABELS.unknown
}

/** A UTC date as the short form the lists use. Empty for an absent or unparseable date. */
export function dateLabel(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The thread route an "Open email" action goes to. Null when the row names no thread. */
export function threadRoute(timelineId) {
  return timelineId ? `/emails/timeline/${timelineId}` : null
}

/** How many items an order has, said in words. '' when the lines were never extracted. */
export function itemsLabel(items) {
  const count = (items || []).length
  if (!count) return ''
  const first = String((items[0] && items[0].name) || '').trim()
  if (count === 1) return first
  return `${first} + ${count - 1} more`
}

/**
 * One order as the list draws it.
 *
 * `subtitle` is the line under the title, and it is built from what is actually known rather than
 * from a template with holes in it: an order with no items and no delivery date says "Placed", not
 * "Placed ·  · ".
 */
export function orderRow(order) {
  if (!order) return null
  const status = String(order.status || 'placed').toLowerCase()
  const amount = formatMoney(order.amount, order.currency)
  const delivery = dateLabel(order.expectedDeliveryUtc)
  const timelineIds = order.timelineIds || []

  return {
    id: order.id,
    kind: 'order',
    title: order.merchant || order.orderNumber || 'Order',
    orderNumber: order.orderNumber || '',
    status,
    statusLabel: statusLabel(status),
    statusTone: statusTone(status),
    amount,
    items: order.items || [],
    itemsLabel: itemsLabel(order.items),
    deliveryLabel: delivery ? `Expected ${delivery}` : '',
    trackingUrl: order.trackingUrl || null,
    timelineIds,
    timelineId: timelineIds[0] || null,
    threadRoute: threadRoute(timelineIds[0]),
    subtitle: joined([order.orderNumber && `#${order.orderNumber}`, itemsLabel(order.items), amount]),
    updatedUtc: order.updatedUtc,
  }
}

/** One receipt as the list draws it. */
export function receiptRow(receipt) {
  if (!receipt) return null
  return {
    id: receipt.id,
    kind: 'receipt',
    title: receipt.merchant || 'Receipt',
    amount: formatMoney(receipt.amount, receipt.currency),
    dateLabel: dateLabel(receipt.dateUtc),
    category: receipt.category || '',
    paymentMethod: receipt.paymentMethod || '',
    reference: receipt.reference || '',
    timelineId: receipt.timelineId || null,
    threadRoute: threadRoute(receipt.timelineId),
    subtitle: joined([dateLabel(receipt.dateUtc), receipt.category, receipt.paymentMethod]),
    updatedUtc: receipt.updatedUtc,
  }
}

/** One subscription source as the list draws it. */
export function subscriptionRow(subscription) {
  if (!subscription) return null
  const count = Number(subscription.count || 0)
  return {
    id: subscription.id,
    kind: 'subscription',
    title: subscription.name || subscription.senderAddress || 'Sender',
    sender: subscription.senderAddress || '',
    frequency: String(subscription.frequency || 'unknown').toLowerCase(),
    frequencyLabel: frequencyLabel(subscription.frequency),
    count,
    countLabel: count === 1 ? '1 email' : `${count} emails`,
    lastSeenLabel: dateLabel(subscription.lastSeenUtc),
    // The server computes this too; recomputing here keeps the row honest when the field is missing.
    canUnsubscribe: !!subscription.unsubscribeUrl,
    unsubscribeUrl: subscription.unsubscribeUrl || null,
    archivedAll: !!subscription.archivedAll,
    engagement: subscription.engagement ?? null,
    subtitle: joined([subscription.senderAddress, frequencyLabel(subscription.frequency), count ? (count === 1 ? '1 email' : `${count} emails`) : '']),
  }
}

/**
 * The actions an order row offers (spec Appendix §7). Actions are NAMES with a label and an enabled
 * flag — never closures — so a row stays a plain value and the view decides what each name does.
 * A disabled action is still listed: "Track order" greyed out says the shop did not send a link,
 * while a missing one says nothing at all.
 */
export function orderActions(row) {
  return [
    { name: 'open', label: 'Open email', enabled: !!row?.timelineId },
    { name: 'track', label: 'Track order', enabled: !!row?.trackingUrl },
    { name: 'receipt', label: 'View receipt', enabled: !!row?.timelineId },
    { name: 'ongoing', label: 'Add to Ongoing', enabled: !!row?.timelineId },
    { name: 'task', label: 'Create task', enabled: true },
  ]
}

/**
 * The RECEIPT an order was paid by, out of the receipts already loaded — what "View receipt" opens
 * (spec Appendix §7; busy-my-web#292, where that action was the same click as "Open email").
 *
 * Matched on the order number first, because a reference is an identity, and only then on the
 * merchant, because a shop the user bought from once has one receipt and naming it is better than
 * naming nothing. Null when neither matches — the caller then falls back to the order's own thread,
 * which is where the confirmation (and usually the receipt with it) actually is.
 *
 * Takes ROWS on both sides, so it reads the same fields the lists draw.
 */
export function receiptForOrder(order, receipts) {
  if (!order) return null
  const list = (receipts || []).filter(Boolean)
  const number = String(order.orderNumber || '').trim().toLowerCase()
  const merchant = String(order.title || '').trim().toLowerCase()
  const byReference = number
    ? list.find(r => String(r.reference || '').trim().toLowerCase() === number)
    : null
  if (byReference) return byReference
  if (!merchant) return null
  return list.find(r => String(r.title || '').trim().toLowerCase() === merchant) || null
}

/** The actions a receipt row offers (spec Appendix §8). */
export function receiptActions(row) {
  return [
    // "Open receipt", not "Open": on the Briefing's own page every row has an Open, and the word
    // that tells the user WHAT opens is the one the spec writes (Appendix §8).
    { name: 'open', label: 'Open receipt', enabled: !!row?.timelineId },
    { name: 'ongoing', label: 'Add to Ongoing', enabled: !!row?.timelineId },
    { name: 'export', label: 'Export', enabled: true },
    { name: 'archive', label: 'Archive', enabled: !!row?.timelineId },
  ]
}

/** The actions a subscription row offers (spec Appendix §9). */
export function subscriptionActions(row) {
  return [
    { name: 'view', label: 'View emails', enabled: (row?.count || 0) > 0 },
    { name: 'archiveAll', label: 'Archive all', enabled: (row?.count || 0) > 0 },
    { name: 'markAllRead', label: 'Mark all read', enabled: (row?.count || 0) > 0 },
    { name: 'unsubscribe', label: 'Unsubscribe', enabled: !!row?.canUnsubscribe },
  ]
}

/**
 * What a set of receipts adds up to, per currency. Money of two currencies has no sum, and a
 * single total under mixed rows would be a wrong number in a place people trust numbers.
 */
export function receiptTotals(receipts) {
  const byCurrency = new Map()
  for (const r of receipts || []) {
    const code = String(r.currency || '').trim().toUpperCase()
    const value = Number(r.amount)
    if (!Number.isFinite(value)) continue
    byCurrency.set(code, (byCurrency.get(code) || 0) + value)
  }
  return [...byCurrency.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([currency, amount]) => ({ currency, amount, label: formatMoney(amount, currency) }))
}

/** The three list routes, so the views and any future entry point name them once. */
export const COMMERCE_ROUTES = {
  orders: '/emails/orders',
  receipts: '/emails/receipts',
  subscriptions: '/emails/subscriptions',
}

/**
 * The Briefing's three entries, from the counts busy-timeline-srv#22 already answers
 * (`v1/threads/classifications/counts`). Rows with a count of zero are dropped, because a briefing
 * states what IS there — the same rule the briefing builder itself follows.
 *
 * Kept here rather than in lib/briefings/email.js so the Briefing lane can adopt it in one line.
 */
export function commerceBriefingRows(counts) {
  const rows = [
    { key: 'orders', icon: 'box', count: Number(counts?.orders || 0), label: 'order', route: COMMERCE_ROUTES.orders },
    { key: 'receipts', icon: 'receipt-item', count: Number(counts?.receipts || 0), label: 'receipt', route: COMMERCE_ROUTES.receipts },
    { key: 'subscriptions', icon: 'sms-notification', count: Number(counts?.subscriptions || 0), label: 'subscription', route: COMMERCE_ROUTES.subscriptions },
  ]
  return rows
    .filter(r => r.count > 0)
    .map(r => ({ key: r.key, icon: r.icon, text: `${r.count} ${r.label}${r.count === 1 ? '' : 's'}`, route: r.route }))
}

// ── The Briefing entries these three lists ARE (busy-my-web#292) ────────────────────────────────
//
// The Email Briefing's `orders|receipts|subscriptions` rows used to open the classification THREAD
// list — the mail a classifier called an order — while these lists, and every action on them
// (Track order, Export, Unsubscribe), were reachable only by typing the URL. They are the same three
// subjects, so the entry now opens the list and keeps the threads as a second tab under it.

/** The three Briefing entries that are a commerce list rather than a plain thread list. */
export const COMMERCE_ENTRIES = ['orders', 'receipts', 'subscriptions']

/** Is this Briefing entry key one of them? Anything else is a thread list and is left alone. */
export function isCommerceEntry(key) {
  return COMMERCE_ENTRIES.includes(String(key || ''))
}

/**
 * The entry a COMMERCE ROUTE is (busy-my-web#562): '/emails/subscriptions' → 'subscriptions'.
 *
 * The three lists are a level INSIDE the Email workspace — reached from the Briefing's rows, which
 * open them in place — but each also has a URL of its own, and a cold load of one (a reload, a
 * bookmark, or coming back from an external page) has no session behind it. The box then had no
 * "← Back" at all, because its chrome is read off the session (lib/boxChrome.js) and there was
 * none: the page was a listing with nowhere to step out to. Derived from the ROUTE rather than
 * restored from storage — the URL already says which level this is, so nothing has to be persisted
 * across the reload.
 *
 * Returns '' for every other path, so the caller can simply ask.
 */
export function commerceRouteEntry(path) {
  const p = String(path || '').split('?')[0].replace(/\/+$/, '')
  const hit = COMMERCE_ENTRIES.find(key => COMMERCE_ROUTES[key] === p)
  return hit || ''
}

/** The commerce store's kind for an entry — the same word, named once so no view spells it twice. */
export function commerceKind(key) {
  return isCommerceEntry(key) ? String(key) : ''
}

/**
 * The two tabs inside an open commerce entry (spec §6): the LIST — "27 orders", what the entry is
 * called everywhere else — and the classification threads it was read from, "emails".
 *
 * `full` is the tab's own long form ("27 orders · emails"), which is what a title attribute and the
 * breadcrumb-free narrow layouts say; `label` is what the tab itself draws.
 *
 * @param entryLabel the entry's counted words ("27 orders"), from lib/briefings/email.js
 */
export function commerceTabs(entryLabel) {
  const head = String(entryLabel || '').trim() || 'List'
  return [
    { key: 'commerce', label: head, full: head },
    { key: 'emails', label: 'emails', full: `${head} · emails` },
  ]
}

/**
 * The counts the Briefing's three commerce rows should state.
 *
 * `GET v1/email/briefing` answers them from the CLASSIFICATION store (MainBff
 * EmailBriefingService.Counts → `_classifications.Counts()`), which counts THREADS: an order
 * confirmed, shipped and delivered is three of them and one order. The list the row now opens is the
 * aggregate — one row per order, per receipt, per sender — so the row promised 27 and would open 9.
 *
 * The rule is that a row names WHAT THE ENTRY OPENS:
 *
 *   • an aggregate in hand with something in it → its own total, because that is the list you land
 *     on;
 *   • an aggregate that is empty, or not read yet, or refused → the classification count, because
 *     what is there then is the EMAILS, which is exactly what the entry's second tab shows. Taking
 *     the row down instead (a count of zero is not drawn) would hide 27 real emails from the
 *     Briefing because an extractor had not caught up with them.
 *
 * Pure: `totals` is `{ orders, receipts, subscriptions }` with a number for each list that has been
 * loaded and nothing for the ones that have not.
 */
export function reconcileCommerceCounts(counts, totals) {
  if (!counts || typeof counts !== 'object') return counts ?? null
  const merged = { ...counts }
  for (const key of COMMERCE_ENTRIES) {
    const total = Number(totals?.[key])
    if (Number.isFinite(total) && total > 0) merged[key] = total
  }
  return merged
}

/** The non-empty parts of a subtitle, joined the way the design joins them. */
function joined(parts) {
  return (parts || []).map(p => String(p ?? '').trim()).filter(Boolean).join(' · ')
}
